import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ExerciseMediaSources {
  videoUrl: string | null;
  gifUrl: string | null;
  thumbnailUrl: string | null;
}

interface ExerciseMedia extends ExerciseMediaSources {
  loading: boolean;
  error: string | null;
  retry: () => void;
}

const EMPTY: ExerciseMediaSources = { videoUrl: null, gifUrl: null, thumbnailUrl: null };
const MEDIA_TTL = 5 * 60 * 1000;
const EMPTY_MEDIA_TTL = 30 * 1000;
const mediaCache = new Map<string, { sources: ExerciseMediaSources; expiresAt: number }>();
const requestGeneration = new Map<string, number>();
const mediaKey = (name: string) => name.trim().toLowerCase();
const source = (value: unknown): string | null => typeof value === "string" && value.trim() ? value.trim() : null;
const sourcesFromRow = (row: Record<string, unknown> | null): ExerciseMediaSources => ({
  videoUrl: source(row?.video_url), gifUrl: source(row?.gif_url), thumbnailUrl: source(row?.thumbnail_url),
});
const cachedSources = (key: string) => {
  const cached = mediaCache.get(key);
  if (!cached || Date.now() >= cached.expiresAt) {
    mediaCache.delete(key);
    return null;
  }
  return cached.sources;
};
const cacheSources = (key: string, sources: ExerciseMediaSources, generation: number) => {
  if (requestGeneration.get(key) !== generation) return;
  const ttl = sources.videoUrl || sources.gifUrl || sources.thumbnailUrl ? MEDIA_TTL : EMPTY_MEDIA_TTL;
  mediaCache.set(key, { sources, expiresAt: Date.now() + ttl });
};
const beginRequest = (key: string) => {
  const generation = (requestGeneration.get(key) ?? 0) + 1;
  requestGeneration.set(key, generation);
  return generation;
};
// Public catalog lookup; escape ILIKE metacharacters so an exercise name is literal.
const literalPattern = (name: string) => name.replace(/[\\%_]/g, "\\$&");

export function useExerciseMedia(exerciseNameEn: string, fallbackGifUrl?: string): ExerciseMedia {
  const name = exerciseNameEn.trim();
  const key = mediaKey(name);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ key: string; sources: ExerciseMediaSources; loading: boolean; error: string | null }>({
    key: "", sources: EMPTY, loading: false, error: null,
  });
  const retry = useCallback(() => {
    mediaCache.delete(key);
    setAttempt(value => value + 1);
  }, [key]);

  useEffect(() => {
    if (!key) {
      setState({ key, sources: EMPTY, loading: false, error: null });
      return;
    }
    const cached = cachedSources(key);
    if (cached) {
      setState({ key, sources: cached, loading: false, error: null });
      return;
    }
    let cancelled = false;
    const generation = beginRequest(key);
    setState({ key, sources: EMPTY, loading: true, error: null });
    const load = async () => {
      try {
        const query = supabase.from("exercise_videos")
          .select("video_url, gif_url, thumbnail_url");
        // PostgREST treats * as a wildcard alias even in LIKE patterns. For
        // those rare names use literal equality rather than guessing its escape.
        const filter = name.includes("*")
          ? query.eq("exercise_name_en", name)
          : query.ilike("exercise_name_en", literalPattern(name));
        const { data, error } = await filter.maybeSingle();
        if (cancelled) return;
        if (error) throw error;
        const sources = sourcesFromRow(data);
        cacheSources(key, sources, generation);
        setState({ key, sources, loading: false, error: null });
      } catch {
        if (cancelled) return;
        console.warn("[Fitness media] exercise lookup unavailable");
        setState({ key, sources: EMPTY, loading: false, error: "unavailable" });
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [key, name, attempt]);

  const current = state.key === key ? state : { sources: EMPTY, loading: !!key, error: null };
  return { ...current.sources, gifUrl: current.sources.gifUrl || source(fallbackGifUrl),
    loading: current.loading, error: current.error, retry };
}

/** Batch-fetch the public catalog. Caller fallbacks never enter the shared cache. */
export function useExerciseMediaBatch(exerciseNames: string[], fallbackGifs?: Record<string, string>): {
  mediaMap: Record<string, ExerciseMediaSources>;
  loading: boolean;
  error: string | null;
  retry: () => void;
} {
  const signature = JSON.stringify([...new Set(exerciseNames.map(mediaKey).filter(Boolean))].sort());
  const names = useMemo(() => {
    const unique = new Map<string, string>();
    exerciseNames.forEach(name => { const key = mediaKey(name); if (key && !unique.has(key)) unique.set(key, name.trim()); });
    return unique;
    // The normalized signature owns the request; casing and ordering only change its presentation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ signature: string; sources: Record<string, ExerciseMediaSources>; loading: boolean; error: string | null }>({
    signature: "[]", sources: {}, loading: false, error: null,
  });
  const retry = useCallback(() => {
    names.forEach((_, key) => mediaCache.delete(key));
    setAttempt(value => value + 1);
  }, [names]);

  useEffect(() => {
    if (names.size === 0) {
      setState({ signature, sources: {}, loading: false, error: null });
      return;
    }
    let cancelled = false;
    const result: Record<string, ExerciseMediaSources> = {};
    const uncached: { name: string; key: string; generation: number }[] = [];
    names.forEach((name, key) => {
      const cached = cachedSources(key);
      if (cached) result[key] = cached;
      else uncached.push({ name, key, generation: beginRequest(key) });
    });
    setState({ signature, sources: result, loading: uncached.length > 0, error: null });
    if (!uncached.length) return;
    const load = async () => {
      try {
        const { data, error } = await supabase.from("exercise_videos")
          .select("exercise_name_en, video_url, gif_url, thumbnail_url")
          .or(uncached.map(item => item.name.includes("*")
            ? "exercise_name_en.eq." + JSON.stringify(item.name)
            : "exercise_name_en.ilike." + JSON.stringify(literalPattern(item.name))).join(","));
        if (cancelled) return;
        if (error) throw error;
        const byName = new Map((data ?? []).map(row => [mediaKey(row.exercise_name_en), row]));
        for (const { key, generation } of uncached) {
          const sources = sourcesFromRow(byName.get(key) ?? null);
          cacheSources(key, sources, generation);
          result[key] = sources;
        }
        setState({ signature, sources: result, loading: false, error: null });
      } catch {
        if (cancelled) return;
        console.warn("[Fitness media] catalog lookup unavailable");
        setState({ signature, sources: result, loading: false, error: "unavailable" });
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [signature, names, attempt]);

  const current = state.signature === signature ? state : { sources: {}, loading: names.size > 0, error: null };
  const mediaMap: Record<string, ExerciseMediaSources> = {};
  exerciseNames.forEach(name => {
    if (!mediaKey(name)) return;
    const sources = current.sources[mediaKey(name)] ?? EMPTY;
    mediaMap[name] = { ...sources, gifUrl: sources.gifUrl || source(fallbackGifs?.[name]) };
  });
  return { mediaMap, loading: current.loading, error: current.error, retry };
}
